import {
	AlphabetSortAsc,
	AlphabetSortDesc,
	AlphabetSortHover,
	NumericSortAsc,
	NumericSortDesc,
	NumericSortHover,
	StandardSortAsc,
	StandardSortDesc,
	StandardSortHover,
} from '~/components/icons'
import { PATHS } from './path.constant'

export const HEADER_SORT_DATA_TYPE = {
	alphabet: 'alphabet',
	numeric: 'numeric',
	standard: 'standard',
	multiSelect: 'multi-select',
	range: 'range',
	other: 'other',
}

/**
 * Does a click on this column's header sort it? `sortable: true` also marks a
 * column whose header carries a multi-select filter (`sortDataType: multiSelect`),
 * and the header handler ignores those, so the pointer cursor must follow this
 * rule, not `sortable` alone — or the header looks clickable and does nothing.
 */
export const sortsOnHeaderClick = (column) => Boolean(column?.sortable) && column?.sortDataType !== HEADER_SORT_DATA_TYPE.multiSelect

export const HEADER_SORT_DATA_TYPE_ICON = {
	[HEADER_SORT_DATA_TYPE.alphabet]: {
		hover: AlphabetSortHover,
		asc: AlphabetSortAsc,
		desc: AlphabetSortDesc,
	},
	[HEADER_SORT_DATA_TYPE.numeric]: {
		hover: NumericSortHover,
		asc: NumericSortAsc,
		desc: NumericSortDesc,
	},
	[HEADER_SORT_DATA_TYPE.standard]: {
		hover: StandardSortHover,
		asc: StandardSortAsc,
		desc: StandardSortDesc,
	},
}

export const JOB_SUBMISSION_STAGE = {
	newApplication: 'job_applied',
	submitted: 'internal_sub',
	exSubmitted: 'external_sub',
	scheduleInterview: 'schedule_interview',
	offered: 'offered',
	placed: 'placed',
	inReview: 'in_review',
	interview: 'interview',
}
export const JOB_SUBMISSION_STAGE_BADGE_COLOR = {
	[JOB_SUBMISSION_STAGE.newApplication]: {
		textColor: '#7E7E80',
		bgColor: '#F5F5F5',
	},
	[JOB_SUBMISSION_STAGE.scheduleInterview]: {
		textColor: '#F29100',
		bgColor: '#FFF6DE',
	},
	[JOB_SUBMISSION_STAGE.submitted]: {
		textColor: '#0F58E4',
		bgColor: '#EFF4FF',
	},
	[JOB_SUBMISSION_STAGE.exSubmitted]: {
		textColor: '#0F58E4',
		bgColor: '#EFF4FF',
	},
	[JOB_SUBMISSION_STAGE.offered]: {
		textColor: '#37AA00',
		bgColor: '#ECFDF3',
	},
	[JOB_SUBMISSION_STAGE.placed]: {
		textColor: '#37AA00',
		bgColor: '#ECFDF3',
	},
	[JOB_SUBMISSION_STAGE.inReview]: {
		textColor: '#0F58E4',
		bgColor: '#EFF4FF',
	},
	[JOB_SUBMISSION_STAGE.interview]: {
		textColor: '#0F58E4',
		bgColor: '#EFF4FF',
	},
}

/**
 * Rendered wherever a cell has no value.
 *
 * Was the literal string 'N/A'. Three words' worth of ink for "nothing here"
 * reads as loud as real data — on a tab where most rows have no engagement
 * score, the eye lands on the placeholders first. An em dash is the
 * conventional typographic "no value" and recedes instead of competing.
 * `<EmptyCell />` renders it muted; this constant stays a plain string because
 * ~90 call sites interpolate it into tooltip titles, not JSX.
 */
export const EMPTY_CELL_DATA = '—'
export const CANDIDATE_PROFILE_COMPLETED = 100

/**
 * The height of one row: avatar plus two lines of text.
 *
 * There was a second, 56px "compact" height behind a toolbar toggle. It is gone
 * — one row height for everybody. A per-user density knob is a preference the
 * table has to carry in two places (row height AND a `compact` style variant for
 * every cell) to save a reader something they can already get by hiding columns,
 * and the toggle occupied toolbar space next to the controls that do real work.
 */
export const TABLE_ROW_HEIGHT = 84

export const TABLE_HEADER_HEIGHT = 48

/**
 * Where the sticky table header comes to rest: directly under the fixed
 * breadcrumb bar (`recruiter.style.js` → `breadcrumbs`, 50px tall, pinned to
 * top 0 on md+). While impersonating, a 48px banner pushes that bar down and
 * the header has to follow it.
 */
export const TABLE_AFFIX_HEADER_OFFSET = 50
export const TABLE_AFFIX_HEADER_OFFSET_IMPERSONATING = 98

export const redirectTabViewAsCandidate = (worker) => {
	if (worker?.profilePercentageCompleted === CANDIDATE_PROFILE_COMPLETED) {
		return `${PATHS.recruiter.view_as_candidate}/${worker.id}/PROFILE`
	}
	return `${PATHS.recruiter.view_as_candidate}/${worker.id}/ACTIVATION`
}

export const LIMIT_DATA_INNER_TABLE = 10

/**
 * How many rows one infinite-scroll batch asks for.
 *
 * This was the hard-coded `INFINITE_SCROLL_BATCH = 50` inside both table
 * components. It is a per-user setting now for the same reason the mode is: at
 * ~92ms per row on `searchWorkers`, 25 is two seconds and 200 is eighteen, and
 * which of those is the right trade depends entirely on whether you are dipping
 * into a shortlist or working a whole pipeline down.
 *
 * There is deliberately no "all rows" option. The largest tab holds 11,423 rows,
 * which is a single ~17-minute request the user cannot cancel.
 */
export const TABLE_LOAD_BATCH_OPTIONS = [25, 50, 100, 200]
export const DEFAULT_TABLE_LOAD_BATCH = 50

/**
 * How many rows the first fetch asks for.
 *
 * This was 10 while "load more" asked for 50, so a table showing about nine rows
 * arrived already out of data — which is what "it only loads ten items and stops"
 * was describing. Raising it to 50 fixed that and cost far too much: the search
 * query runs at a flat ~92ms per row (25 rows 2.5s, 50 rows 4.6s, 100 rows 8.3s),
 * so matching the load-more batch added roughly 3.7 seconds to every first paint.
 *
 * 25 is the compromise those measurements point at: about three screens of rows,
 * and half the wait. Lowering the per-row cost is the real fix and it lives in
 * the query, not here.
 */
export const INITIAL_TABLE_PAGE_SIZE = 25
